import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { JobEditProductItem, JobEditServiceItem } from '@/models/jobEdit';

// Ordered longest-first so "OUT: STORE" matches before "OUT"
const MODEL_PREFIXES = [
  'OUT: RECYCLE',
  'OUT: RETURN',
  'OUT: STORE',
  'OUT: WORKSHOP',
  'OUT',
  'IN',
] as const;

function parseModelPrefix(fullModel: string): {
  prefix: string;
  model: string;
} {
  for (const prefix of MODEL_PREFIXES) {
    if (fullModel.startsWith(prefix + ': ')) {
      return { prefix, model: fullModel.slice(prefix.length + 2) };
    }
  }
  return { prefix: '-', model: fullModel };
}

function buildModelString(prefix: string | undefined, model: string): string {
  if (!prefix || prefix === '-') return model;
  return `${prefix}: ${model}`;
}

export function adaptProductFromApi(
  apiProduct: openapi._ProductOpenapi,
): JobEditProductItem {
  const { prefix, model } = parseModelPrefix(apiProduct.model || '');
  return {
    prefix,
    model,
    model_id: apiProduct.model_id ?? null,
    serial: apiProduct.serial || '',
    description: apiProduct.description || null,
    pieces: apiProduct.pieces || 1,
    quant_id: apiProduct.quant_id || null,
    type_code: apiProduct.type_code || '',
    weight: apiProduct.weight ?? null,
    width: apiProduct.width ?? null,
    height: apiProduct.height ?? null,
    length: apiProduct.length ?? null,
    volume: apiProduct.volume ?? null,
    line_number: apiProduct.line_number ?? null,
    picking_status: apiProduct.picking_status,
  };
}

export function adaptProductToApi(
  uiProduct: JobEditProductItem,
): Partial<openapi._ProductOpenapi> {
  // Intentionally omits model_id/dimensions — server resolves them from the model string on save.
  return {
    model: buildModelString(uiProduct.prefix, uiProduct.model),
    serial: uiProduct.serial,
    description: uiProduct.description,
    pieces: uiProduct.pieces,
    quant_id: uiProduct.quant_id,
    type_code: uiProduct.type_code,
    // Echo legacy line_number back so BE keeps existing ordering.
    line_number: uiProduct.line_number ?? undefined,
  };
}

export function adaptServiceFromApi(
  apiService: openapi._StopOpenapi['services'][number],
): JobEditServiceItem {
  // Backend currently uses `name` as the service code; if BE ever splits
  // code and human-readable name into distinct fields, distinguish them
  // here instead of mirroring.
  return {
    service_code: apiService.name || '',
    name: apiService.name || '',
    quantity: apiService.quantity || 1,
    second_quantity: apiService.second_quantity || 0,
    scheduled_fee: apiService.scheduled_fee || 0,
    fee: apiService.fee || 0,
    gst: apiService.gst || 0,
    pay: apiService.pay || 0,
  };
}

export function adaptServiceToApi(
  uiService: JobEditServiceItem,
): Partial<openapi._StopOpenapi['services'][number]> {
  return {
    name: uiService.name,
    quantity: uiService.quantity,
    second_quantity: uiService.second_quantity,
    scheduled_fee: uiService.scheduled_fee,
    fee: uiService.fee,
    gst: uiService.gst,
    pay: uiService.pay,
  };
}
